import { useState } from 'react';
import type { ChildDevice, ChildProfile } from '../../api/types.js';
import { ConfirmDialog } from '../../ui/ConfirmDialog.js';
import { ChildCard } from './ChildCard.js';
import { useParentDashboard } from './useParentDashboard.js';

/**
 * Port of Android's `ParentDashboardScreen` — the whole parent app in one
 * page: child profiles, their paired devices, pairing codes, and the
 * Internet-Access controls.
 *
 * What is deliberately *not* here, because it has no web equivalent:
 * Child Mode and the "Elternmodus beenden" override (both exist only
 * because the Android APK also runs on the child's phone — see
 * docs/ARCHITECTURE.md's "Client app modes"), and redeeming a pairing
 * code, which only ever happens on a device that is becoming a child
 * device.
 */
export function DashboardPage({ onLoggedOut }: { onLoggedOut: () => void }) {
  const { state, actions } = useParentDashboard(onLoggedOut);
  const [deviceToRevoke, setDeviceToRevoke] = useState<{ childProfileId: string; device: ChildDevice } | null>(null);
  const [childToDelete, setChildToDelete] = useState<ChildProfile | null>(null);

  return (
    <>
      <header className="topbar">
        <h1 className="topbar__title">Familie</h1>
        <div className="topbar__actions">
          <button type="button" className="button button--text" onClick={actions.refresh}>
            Aktualisieren
          </button>
          <button type="button" className="button button--text" onClick={onLoggedOut}>
            Abmelden
          </button>
        </div>
      </header>

      <main className="page">
        <h2 className="section-title">Kinderprofile</h2>

        <form
          className="new-child"
          onSubmit={(event) => {
            event.preventDefault();
            actions.createChildProfile();
          }}
        >
          <label className="visually-hidden" htmlFor="new-child-name">
            Name des Kindes
          </label>
          <input
            id="new-child-name"
            className="input"
            placeholder="Name des Kindes"
            value={state.newChildName}
            onChange={(event) => actions.onNewChildNameChange(event.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            className="button button--primary"
            disabled={state.isCreatingChild || state.newChildName.trim() === ''}
          >
            Hinzufügen
          </button>
        </form>

        {state.errorMessage && (
          <div className="banner banner--error" role="alert">
            <span>{state.errorMessage}</span>
            <button type="button" className="button button--text" onClick={actions.dismissError}>
              Schliessen
            </button>
          </div>
        )}

        {state.isLoading ? (
          <p className="muted" role="status">
            Lädt…
          </p>
        ) : state.childProfiles.length === 0 ? (
          <p className="muted">Noch keine Kinderprofile angelegt.</p>
        ) : (
          <ul className="child-list">
            {state.childProfiles.map((child) => (
              <ChildCard
                key={child.id}
                child={child}
                devices={state.devicesByChildId[child.id] ?? []}
                pairingCode={state.pairingCodesByChildId[child.id]}
                account={state.internetAccessAccountByChildId[child.id]}
                pendingPairingCode={state.pendingPairingCodeChildId === child.id}
                cancellingPairingCode={state.cancellingPairingCodeChildId === child.id}
                deleting={state.deletingChildProfileId === child.id}
                revokingDeviceId={state.revokingDeviceId}
                savingTopUp={state.savingTopUpChildId === child.id}
                savingDeductBonus={state.savingDeductBonusChildId === child.id}
                savingForceBlocked={state.savingForceBlockedChildId === child.id}
                savingReset={state.savingResetChildId === child.id}
                savingBlockWindow={state.savingBlockWindowChildId === child.id}
                onRequestDelete={() => setChildToDelete(child)}
                onGeneratePairingCode={() => actions.generatePairingCode(child.id)}
                onCancelPairingCode={() => actions.cancelPairingCode(child.id)}
                onRequestRevokeDevice={(device) => setDeviceToRevoke({ childProfileId: child.id, device })}
                onTopUp={() => actions.topUpInternetAccess(child.id)}
                onDeductBonus={() => actions.deductBonusInternetAccess(child.id)}
                onSetForceBlocked={(forceBlocked) => actions.setForceBlocked(child.id, forceBlocked)}
                onReset={() => actions.resetInternetAccess(child.id)}
                onAddBlockWindow={(startTime, endTime) => actions.addBlockWindow(child.id, startTime, endTime)}
                onRemoveBlockWindow={(blockWindowId) => actions.removeBlockWindow(child.id, blockWindowId)}
              />
            ))}
          </ul>
        )}
      </main>

      {deviceToRevoke && (
        <ConfirmDialog
          title="Gerät entkoppeln?"
          body={
            `"${deviceToRevoke.device.deviceLabel}" verliert die Zuordnung zu diesem Kind ` +
            'und muss über einen neuen Pairing-Code erneut gekoppelt werden.'
          }
          confirmLabel="Entkoppeln"
          onConfirm={() => {
            actions.revokeDevice(deviceToRevoke.childProfileId, deviceToRevoke.device.id);
            setDeviceToRevoke(null);
          }}
          onDismiss={() => setDeviceToRevoke(null)}
        />
      )}

      {childToDelete && (
        <ConfirmDialog
          title={`"${childToDelete.displayName}" löschen?`}
          body={
            'Das entfernt endgültig alle Geräte, Pairing-Codes und das Internet-Guthaben ' +
            'dieses Kindes. Das kann nicht rückgängig gemacht werden.'
          }
          confirmLabel="Endgültig löschen"
          destructive
          onConfirm={() => {
            actions.deleteChildProfile(childToDelete.id);
            setChildToDelete(null);
          }}
          onDismiss={() => setChildToDelete(null)}
        />
      )}
    </>
  );
}
